import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { projectsApi, projectTagsApi } from '../utils/api';
import type { ProjectListItem, ProjectTagTreeNode } from '../types/project';
import Navigation from '../components/Navigation';
import { useSmartSticky } from '../hooks/useSmartSticky';

// Tag Tree Node Component
interface TagTreeNodeProps {
  node: ProjectTagTreeNode;
  level: number;
  selectedTagId: number | null;
  onSelectTag: (tagId: number | null) => void;
}

function TagTreeNode({ node, level, selectedTagId, onSelectTag }: TagTreeNodeProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const hasChildren = node.children && node.children.length > 0;
  const isSelected = selectedTagId === node.id;

  return (
    <div>
      <div
        className={`flex items-center py-1.5 px-2 rounded cursor-pointer hover:bg-gray-100 ${
          isSelected ? 'bg-gray-200 font-medium' : ''
        }`}
        style={{ paddingLeft: `${level * 16 + 8}px` }}
        onClick={() => onSelectTag(isSelected ? null : node.id)}
      >
        {hasChildren ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
            className="mr-1.5 w-4 h-4 flex items-center justify-center text-gray-400 hover:text-gray-600"
          >
            {isExpanded ? '▼' : '▶'}
          </button>
        ) : (
          <span className="mr-1.5 w-4" />
        )}

        {node.color && (
          <span
            className="w-3 h-3 rounded-full mr-1.5 flex-shrink-0"
            style={{ backgroundColor: node.color }}
          />
        )}

        <span className="flex-1 text-sm truncate">{node.name}</span>
        <span className="text-xs text-gray-400 ml-1">{node.project_count}</span>
      </div>

      {hasChildren && isExpanded && (
        <div>
          {node.children.map((child) => (
            <TagTreeNode
              key={child.id}
              node={child}
              level={level + 1}
              selectedTagId={selectedTagId}
              onSelectTag={onSelectTag}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function ProjectsPage() {
  const [projects, setProjects] = useState<ProjectListItem[]>([]);
  const [tagTree, setTagTree] = useState<ProjectTagTreeNode[]>([]);
  const [selectedTagId, setSelectedTagId] = useState<number | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // Smart sticky sidebar
  const sidebarRef = useSmartSticky(96, 16);

  const loadProjects = async () => {
    try {
      setIsLoading(true);
      const data = await projectsApi.getAll(showArchived);
      setProjects(data);
      setError('');
    } catch (err) {
      setError('Failed to load projects');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const loadTags = async () => {
    try {
      const data = await projectTagsApi.getTree();
      setTagTree(data);
    } catch (err) {
      console.error('Failed to load tags:', err);
    }
  };

  const loadProjectsByTag = async (tagId: number) => {
    try {
      setIsLoading(true);
      const data = await projectTagsApi.getProjectsByTag(tagId);
      setProjects(data);
      setError('');
    } catch (err) {
      setError('Failed to load projects by tag');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle tag selection
  const handleSelectTag = (tagId: number | null) => {
    setSelectedTagId(tagId);
    // Scroll to top to show header and start of filtered list
    window.scrollTo(0, 0);
  };

  // Get selected tag path for title (returns array of tag names from root to selected)
  const getSelectedTagPath = (): string[] | null => {
    if (selectedTagId === null) return null;
    const findTagPath = (nodes: ProjectTagTreeNode[], path: string[] = []): string[] | null => {
      for (const node of nodes) {
        const currentPath = [...path, node.name];
        if (node.id === selectedTagId) return currentPath;
        const found = findTagPath(node.children, currentPath);
        if (found) return found;
      }
      return null;
    };
    return findTagPath(tagTree);
  };

  useEffect(() => {
    loadTags();
  }, []);

  useEffect(() => {
    if (selectedTagId !== null) {
      loadProjectsByTag(selectedTagId);
    } else {
      loadProjects();
    }
  }, [showArchived, selectedTagId]);

  if (isLoading && projects.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading projects...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Projects - MAIA Agent 128</title>
      </Helmet>
      <div className="min-h-screen bg-gray-50">
        <Navigation />

      <div className="max-w-6xl mx-auto px-4 pt-24 pb-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold">
            {selectedTagId !== null && getSelectedTagPath() ? (
              <>
                Tag:{' '}
                {getSelectedTagPath()!.map((name, index, arr) => (
                  <span key={index}>
                    {index < arr.length - 1 ? (
                      <span className="text-lg font-normal text-gray-500">{name} / </span>
                    ) : (
                      <span>{name}</span>
                    )}
                  </span>
                ))}
              </>
            ) : (
              'Projects'
            )}
          </h1>
          <div className="flex items-center space-x-3">
            <Link
              to="/project-tags"
              className="px-4 py-2 btn-secondary rounded-md text-sm font-medium"
            >
              Manage Tags
            </Link>
            <Link
              to="/projects/new"
              className="px-4 py-2 btn-primary rounded-md text-sm font-medium"
            >
              New Project
            </Link>
          </div>
        </div>

        <div className="mb-6 flex items-center space-x-4">
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="rounded border-gray-300"
            />
            <span className="text-sm text-gray-700">Show Archived</span>
          </label>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}
      </div>

      {/* Content Area with Sidebar */}
      <div className="max-w-7xl mx-auto px-4 pb-8">
        <div className="flex gap-6">
          {/* Tag Tree Sidebar */}
          <div className="w-64 flex-shrink-0">
            <div
              ref={sidebarRef}
              className="bg-white border border-gray-200 rounded-lg p-4"
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-medium text-gray-900">Tags</h3>
                {selectedTagId !== null && (
                  <button
                    onClick={() => handleSelectTag(null)}
                    className="text-xs text-gray-500 hover:text-gray-700"
                  >
                    Clear
                  </button>
                )}
              </div>
              {tagTree.length === 0 ? (
                <p className="text-sm text-gray-500">No tags yet</p>
              ) : (
                <div className="space-y-0.5">
                  {tagTree.map((node) => (
                    <TagTreeNode
                      key={node.id}
                      node={node}
                      level={0}
                      selectedTagId={selectedTagId}
                      onSelectTag={handleSelectTag}
                    />
                  ))}
                </div>
              )}
              <Link
                to="/project-tags"
                className="block mt-4 text-xs text-center text-gray-500 hover:text-gray-700"
              >
                Manage Tags →
              </Link>
            </div>
          </div>

          {/* Projects List */}
          <div className="flex-1 min-w-0">
            {projects.length === 0 ? (
              <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
                <p className="text-gray-500">
                  {selectedTagId !== null ? 'No projects with this tag' : showArchived ? 'No archived projects' : 'No projects yet'}
                </p>
                {!showArchived && selectedTagId === null && (
                  <Link
                    to="/projects/new"
                    className="inline-block mt-4 px-4 py-2 btn-primary rounded-md text-sm font-medium"
                  >
                    Create Your First Project
                  </Link>
                )}
              </div>
            ) : (
              <div className="grid gap-4">
                {projects.map((project) => (
                  <Link
                    key={project.id}
                    to={`/projects/${project.id}`}
                    className="bg-white border border-gray-200 rounded-lg p-6 card-hover shadow-subtle relative"
                  >
                    {project.archived && (
                      <div className="absolute top-4 right-4">
                        <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                          Archived
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <h3 className="text-lg font-medium text-gray-900">{project.name}</h3>
                        {project.description && (
                          <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                            {project.description}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <span>{project.task_count} task{project.task_count !== 1 ? 's' : ''}</span>
                        <span>{project.kb_count} KB{project.kb_count !== 1 ? 's' : ''}</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>Created: {new Date(project.created_at).toLocaleDateString()}</span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
    </>
  );
}
